import { ItemFormConfigCategorySelection } from "~fulfill/product/items/helpers/validateItemForm";

export interface ConfigCategoryIdentity {
	code: string;
	containerConfigIds?: readonly string[];
	configCategoryId: string;
	defaultSizeUomCode?: string;
	name: string;
}

interface ConfigCategoryIndices<T> {
	byCode: ReadonlyMap<string, T>;
	byId: ReadonlyMap<string, T>;
	byName: ReadonlyMap<string, T>;
}

export const configCategoryKey = (category: ConfigCategoryIdentity): string =>
	category.code || category.configCategoryId;

const configCategoryIndicesByArray = new WeakMap<
	readonly ConfigCategoryIdentity[],
	ConfigCategoryIndices<ConfigCategoryIdentity>
>();

const buildConfigCategoryIndices = <T extends ConfigCategoryIdentity>(
	configCategories: readonly T[]
): ConfigCategoryIndices<T> => {
	const byCode = new Map<string, T>();
	const byId = new Map<string, T>();
	const byName = new Map<string, T>();
	for (const category of configCategories) {
		if (category.code) byCode.set(category.code, category);
		if (category.configCategoryId) byId.set(category.configCategoryId, category);
		if (category.name) byName.set(category.name, category);
	}
	return { byCode, byId, byName };
};

const indexConfigCategories = <T extends ConfigCategoryIdentity>(
	configCategories: readonly T[]
): ConfigCategoryIndices<T> => {
	const cached = configCategoryIndicesByArray.get(configCategories) as ConfigCategoryIndices<T> | undefined;
	if (cached) return cached;
	const indices = buildConfigCategoryIndices(configCategories);
	configCategoryIndicesByArray.set(configCategories, indices);
	return indices;
};

export const findConfigCategory = <T extends ConfigCategoryIdentity>(
	value: string,
	configCategories: readonly T[]
): T | undefined => {
	if (!value) return undefined;
	const { byCode, byId, byName } = indexConfigCategories(configCategories);
	return byId.get(value) ?? byCode.get(value) ?? byName.get(value);
};

// wsapi's ConfigCategoryResolution trims and folds case, so the preview must too.
export const findConfigCategoryIgnoringCase = <T extends ConfigCategoryIdentity>(
	value: string,
	configCategories: readonly T[]
): T | undefined => {
	const trimmed = value.trim();
	if (!trimmed) return undefined;
	const exact = findConfigCategory(trimmed, configCategories);
	if (exact) return exact;

	const folded = trimmed.toLowerCase();
	const matches = (candidate: string | undefined): boolean => !!candidate && candidate.toLowerCase() === folded;
	return (
		configCategories.find((category) => matches(category.configCategoryId)) ??
		configCategories.find((category) => matches(category.code)) ??
		configCategories.find((category) => matches(category.name))
	);
};

export const resolveConfigCategoryName = (value: string, configCategories: readonly ConfigCategoryIdentity[]): string =>
	findConfigCategory(value, configCategories)?.name ?? value;

export const resolveConfigCategoryCode = (value: string, configCategories: readonly ConfigCategoryIdentity[]): string =>
	findConfigCategory(value, configCategories)?.code || value;

export const sizeUomCodeToSave = (
	sizeUomCode: string,
	categoryCode: string,
	savedCategoryCode: string,
	category: ConfigCategoryIdentity | undefined
): string => (categoryCode === savedCategoryCode ? sizeUomCode : sizeUomCode || category?.defaultSizeUomCode || "");

/**
 * A Shipping UOM reads as itself. Its unit record is a legacy pointer several UOMs can share — naming a
 * UOM after that pointer made six distinct ones on red all read "4IN", so it is the last resort here.
 */
export const shippingUomLabelForCategory = (category: ConfigCategoryIdentity): string =>
	category.code || category.name || category.defaultSizeUomCode || "";

export const resolveShippingUomLabel = (value: string, configCategories: readonly ConfigCategoryIdentity[]): string => {
	const category = findConfigCategory(value, configCategories);
	return (category && shippingUomLabelForCategory(category)) || value;
};

export const resolveContainerConfigShippingUomLabel = (
	containerConfigId: string,
	categoryCode: string,
	configCategories: readonly ConfigCategoryIdentity[]
): string => {
	const category = findContainerConfigShippingCategory(containerConfigId, categoryCode, configCategories);
	return (category && shippingUomLabelForCategory(category)) || categoryCode;
};

export const resolveShippingUomLabels = (
	values: readonly string[],
	configCategories: readonly ConfigCategoryIdentity[]
): string[] => values.map((value) => resolveShippingUomLabel(value, configCategories));

export const formatShippingUomLabels = (
	values: readonly string[],
	configCategories: readonly ConfigCategoryIdentity[]
): string => resolveShippingUomLabels(values, configCategories).join(", ");

export const resolveConfigCategoryId = (value: string, configCategories: readonly ConfigCategoryIdentity[]): string =>
	findConfigCategory(value, configCategories)?.configCategoryId ?? value;

export const resolveConfigCategoryKey = (
	value: string,
	configCategories: readonly ConfigCategoryIdentity[]
): string => {
	const category = findConfigCategory(value, configCategories);
	return category ? configCategoryKey(category) : value;
};

// wsapi stores an order line's shipping config code verbatim with only a character-format check, so the code must be canonical before it is sent.
export const resolveStorableConfigCategoryKey = (
	value: string,
	configCategories: readonly ConfigCategoryIdentity[]
): string => {
	const category = findConfigCategoryIgnoringCase(value, configCategories);
	return category ? configCategoryKey(category) : value;
};

export const resolveConfigCategoryNames = (
	values: readonly string[],
	configCategories: readonly ConfigCategoryIdentity[]
): string[] => values.map((value) => resolveConfigCategoryName(value, configCategories));

export const formatConfigCategoryNames = (
	values: readonly string[],
	configCategories: readonly ConfigCategoryIdentity[]
): string => resolveConfigCategoryNames(values, configCategories).join(", ");

const configCategoryOwnedAliases = (
	category: ConfigCategoryIdentity,
	configCategories: readonly ConfigCategoryIdentity[]
): string[] =>
	[category.code, category.configCategoryId, category.name].filter(
		(alias): alias is string => !!alias && findConfigCategory(alias, configCategories) === category
	);

export const buildConfigCategoryCodeMatchSet = (
	selectedValues: readonly string[],
	configCategories: readonly ConfigCategoryIdentity[]
): string[] => {
	const matchSet = new Set<string>();
	for (const value of selectedValues) {
		if (!value) continue;
		matchSet.add(value);
		const category = findConfigCategory(value, configCategories);
		if (!category) continue;
		for (const alias of configCategoryOwnedAliases(category, configCategories)) matchSet.add(alias);
	}
	return [...matchSet];
};

export const findContainerConfigShippingCategory = <T extends ConfigCategoryIdentity>(
	containerConfigId: string,
	categoryCode: string,
	configCategories: readonly T[]
): T | undefined =>
	configCategories.find((candidate) => candidate.containerConfigIds?.includes(containerConfigId)) ??
	findConfigCategory(categoryCode, configCategories);

export const configCategoryAliases = (category: ConfigCategoryIdentity): string[] => [
	...new Set([category.configCategoryId, category.code, category.name].filter(Boolean))
];

export const buildContainerConfigShippingConfigMatchSet = (
	containerConfigId: string,
	categoryCode: string,
	configCategories: readonly ConfigCategoryIdentity[]
): string[] => {
	const category = findContainerConfigShippingCategory(containerConfigId, categoryCode, configCategories);
	if (!category) return categoryCode ? [categoryCode] : [];
	return [...new Set([...configCategoryOwnedAliases(category, configCategories), categoryCode].filter(Boolean))];
};

export interface ConfigCategorySavePayload {
	codes: string[];
	defaultCode?: string;
	unresolvedNewValues: string[];
}

export const resolveConfigCategorySavePayload = (
	values: readonly string[],
	defaultValue: string | undefined,
	baselineValues: readonly string[],
	configCategories: readonly ConfigCategoryIdentity[]
): ConfigCategorySavePayload => {
	const baselineSet = new Set(baselineValues);
	const resolveOrEcho = (value: string): string | undefined => {
		const category = findConfigCategory(value, configCategories);
		if (category) return configCategoryKey(category);
		return baselineSet.has(value) ? value : undefined;
	};

	const seenCodes = new Set<string>();
	const codes: string[] = [];
	const unresolvedNewValues: string[] = [];
	for (const value of values) {
		const resolved = resolveOrEcho(value);
		if (resolved === undefined) {
			unresolvedNewValues.push(value);
			continue;
		}
		if (!seenCodes.has(resolved)) {
			seenCodes.add(resolved);
			codes.push(resolved);
		}
	}

	return {
		codes,
		defaultCode: defaultValue !== undefined ? resolveOrEcho(defaultValue) : undefined,
		unresolvedNewValues
	};
};

export const buildUnresolvedConfigCategoryMessage = (
	unresolvedValues: readonly string[],
	isStillLoading: boolean
): string =>
	isStillLoading
		? "Config categories are still loading — please wait a moment and try saving again."
		: `Couldn't recognize config categor${unresolvedValues.length === 1 ? "y" : "ies"} "${unresolvedValues.join('", "')}" — remove the chip and try saving again.`;

export const addConfigCategorySelection = (
	selections: readonly ItemFormConfigCategorySelection[],
	code: string,
	configCategories: readonly ConfigCategoryIdentity[]
): ItemFormConfigCategorySelection[] =>
	selections.some((s) => resolveConfigCategoryKey(s.code, configCategories) === code)
		? [...selections]
		: [...selections, { code, isDefault: selections.length === 0 }];

export default resolveConfigCategoryName;
